import React from 'react';
import { Badge, DropdownMenuItem, Flex, ListItem, Text } from '@/components/ui';
import { FormIcon } from '../FormIcon';
import { getFormDisplayName } from '@/utils/formUtils';
import type { StudyForm } from '../types';
import type { FormTreeNode } from '../utils/formTreeUtils';
import {
  buildNestedPathFromIds,
  matchesNestedPath,
} from '../utils/formTreeUtils';
import {
  IconAntennaBars3,
  IconAntennaBars5,
  IconCheck,
} from '@tabler/icons-react';

interface FormSelectorTreeItemProps {
  form: FormTreeNode;
  level: number;
  availableForms: StudyForm[];
  selectedForm?: StudyForm | null;
  nestedPath?: string; // JSON string with nested path structure
  pathFromRoot?: number[]; // Path from root to this node (array of form IDs)
  onSelect: (formId: string, nestedPath: string) => void;
  useName?: boolean;
  matchLevels?: Map<number, 'perfect' | 'good'>;
  matchScores?: Map<number, number>;
}

export const FormSelectorTreeItem: React.FC<FormSelectorTreeItemProps> = ({
  form,
  level,
  availableForms,
  selectedForm,
  nestedPath,
  pathFromRoot = [],
  onSelect,
  useName = false,
  matchLevels = new Map(),
  matchScores = new Map(),
}) => {
  const indentStyle = {
    '--tree-level': String(level || 0),
  } as React.CSSProperties;

  const displayName = useName ? form.name : getFormDisplayName(form);

  // Build current path from root including this node
  const currentPath = [...pathFromRoot, form.id];

  // Check if this form is selected
  // If nested_path exists, compare the path to ensure we highlight only the correct path
  // when a form has multiple parents. Otherwise, fall back to simple ID comparison.
  // IMPORTANT: If nestedPath is provided, we MUST check it - don't show as selected if path doesn't match
  const isSelected = nestedPath
    ? selectedForm?.id === form.id && matchesNestedPath(currentPath, nestedPath)
    : selectedForm?.id === form.id;

  // Check match level
  const matchLevel = matchLevels.get(form.id);
  const matchScore = matchScores.get(form.id);
  const isPerfectMatch = matchLevel === 'perfect';
  const isGoodMatch = matchLevel === 'good';

  return (
    <React.Fragment>
      <DropdownMenuItem asChild>
        <ListItem
          style={indentStyle}
          className="form-selector-tree-item"
          selected={isSelected}
          startIcon={
            <>
              {level > 0 && <div className="form-selector-tree-indicator" />}
              <FormIcon form={form} availableForms={availableForms} />
            </>
          }
          primary={
            <Flex align="center" gap="2" style={{ minWidth: 0 }}>
              <Text
                weight="medium"
                size="2xsmall"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {form.name}
              </Text>
              {isPerfectMatch && matchScore !== undefined && (
                <Badge
                  size="xsmall"
                  variant="surface"
                  color="success"
                  title={`Perfect match (${Math.round(matchScore * 100)}% confidence)`}
                >
                  <IconAntennaBars5 />
                </Badge>
              )}
              {isGoodMatch && !isPerfectMatch && matchScore !== undefined && (
                <Badge
                  size="xsmall"
                  variant="dashed"
                  color="success"
                  title={`Good match (${Math.round(matchScore * 100)}% confidence)`}
                >
                  <IconAntennaBars3 />
                </Badge>
              )}
              <Text
                size="3xsmall"
                color="gray"
                className="form-selector-subform-display-name"
                style={{ flexShrink: 0 }}
              >
                [{form.fieldCount} fields]
              </Text>
            </Flex>
          }
          secondary={
            <Text
              size="3xsmall"
              color="primary"
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayName}
            </Text>
          }
          endIcon={isSelected ? <IconCheck /> : undefined}
          onClick={() => {
            // Use the actual path from root (currentPath) instead of parent chain
            // This ensures we use the correct path when a form has multiple parents
            const nestedPath = buildNestedPathFromIds(
              currentPath,
              availableForms
            );
            onSelect(form.id.toString(), nestedPath);
          }}
        />
      </DropdownMenuItem>

      {/* Render children recursively */}
      {form.children.length > 0 && (
        <div className="form-selector-tree-children-group">
          {form.children.map(child => (
            <FormSelectorTreeItem
              key={child.id}
              form={child}
              level={level + 1}
              availableForms={availableForms}
              selectedForm={selectedForm}
              nestedPath={nestedPath}
              pathFromRoot={currentPath}
              onSelect={onSelect}
              useName={useName}
              matchLevels={matchLevels}
              matchScores={matchScores}
            />
          ))}
        </div>
      )}
    </React.Fragment>
  );
};
